import { useQuery } from '@tanstack/react-query';
import { request, gql } from 'graphql-request';

import { QueryKeys } from 'src/constants/queryKeys';

import { Pair } from '@app/types/pool';
import { useConfig } from '@app/config';
import { ApplicationConfig } from '@app/config/types';
import { useChainId } from '@thirdweb-dev/react';
import { useSupportedChain } from '@app/hooks/other/useSupportedChain';

type QueryParams = {
  tokens: string;
  orderBy: 'created';
  orderDirection: 'desc';
  address: string | undefined;
  search: string;
};

function getQuery(queryParams: QueryParams) {
  if (queryParams.tokens) {
    return gql`
      query {
        pairs(
          where: {
            and: [
              { token0_in: [${queryParams.tokens}] },
              { token1_in: [${queryParams.tokens}] },
              {
                or: [
                  {
                    token0_: {
                      name_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token0_: {
                      symbol_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token1_: {
                      name_contains_nocase: "${queryParams.search}"
                    }
                  }
                  {
                    token1_: {
                      symbol_contains_nocase: "${queryParams.search}"
                    }
                  },
                ] 
              }
            ]
          }
        )
         {
          id
          token0 {
            id
            totalSupply
            symbol
            name
            decimals
            tradeVolumeUSD
            tradeVolume
            totalLiquidity
            txCount
            derivedETH
          }
          token1 {
            id
            totalSupply
            symbol
            name
            decimals
            tradeVolumeUSD
            tradeVolume
            totalLiquidity
            txCount
            derivedETH
          }
          isStable
          reserve0
          reserve1
          reserveETH
          reserveUSD
          token0Price
          token1Price
          volumeUSD
          liquidityPositions(
            where:{
              user: "${queryParams.address ?? ''}"
            }
          ) {
            liquidityTokenBalance
            user {
              id
            }
          }
        }
      }
    `;
  }

  return gql`
      query {
        pairs(
          where: {
            and: [
              {
                or: [
                  {
                    token0_: {
                      name_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token0_: {
                      symbol_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token1_: {
                      name_contains_nocase: "${queryParams.search}"
                    }
                  }
                  {
                    token1_: {
                      symbol_contains_nocase: "${queryParams.search}"
                    }
                  },
                ] 
              }
            ]
          }
        )
         {
          id
          token0 {
            id
            totalSupply
            symbol
            name
            decimals
            tradeVolumeUSD
            tradeVolume
            totalLiquidity
            txCount
            derivedETH
          }
          token1 {
            id
            totalSupply
            symbol
            name
            decimals
            tradeVolumeUSD
            tradeVolume
            totalLiquidity
            txCount
            derivedETH
          }
          isStable
          reserve0
          reserve1
          reserveETH
          reserveUSD
          token0Price
          token1Price
          volumeUSD
          liquidityPositions(
            where:{
              user: "${queryParams.address ?? ''}"
            }
          ) {
            liquidityTokenBalance
            user {
              id
            }
          }
        }
      }
    `;
}

const fetcher = async (
  queryParams: QueryParams,
  config: ApplicationConfig | undefined
) => {
  const query = getQuery(queryParams);

  const res = await request<{ pairs: Pair[] }>(
    `${config?.URLS.subgraphUrl}`,
    query,
    queryParams
  );

  const pools = res?.pairs;

  const result: Pair[] = [];

  config?.CONTENT.poolsOrder.forEach((poolId: string) => {
    const pool = pools?.find(p => p.id.toLowerCase() === poolId.toLowerCase());

    if (pool) {
      result.push(pool);
    }
  });

  const lowered = config?.CONTENT.poolsOrder.map((p: string) =>
    p.toLowerCase()
  );
  const unordered = pools?.filter(p => !lowered?.includes(p.id.toLowerCase()));

  return [...result, ...unordered].filter(
    pool => !config?.CONTENT.hiddenPools.includes(pool.id)
  );
};

export function useVerifiedPoolsList(params: QueryParams) {
  const config = useConfig();
  const chainId = useSupportedChain()?.chainId;

  return useQuery<Pair[] | undefined>(
    [QueryKeys.PAIRS, { params, chainId }],
    () => {
      return fetcher(params, config);
    },
    {
      staleTime: 60000,
      refetchOnWindowFocus: false,
      enabled: !!config?.URLS.subgraphUrl
    }
  );
}
